import { useCallback, useEffect, useRef, useState } from "react";
import { getTrialResult } from "@/api/trial";
import type { TrialResultResponse } from "@/api/trial";

const FAST_POLLING_DURATION_MS = 60_000;
const FAST_POLLING_INTERVAL_MS = 2_000;
const SLOW_POLLING_INTERVAL_MS = 5_000;
const CONNECTION_ERROR_THRESHOLD = 3;

interface UseTrialResultPollingOptions {
  active: boolean;
  expectedOrderIdx: number | null;
  onCompleted: (result: TrialResultResponse) => void;
  onFailed: (result: TrialResultResponse) => void;
}

interface TrialResultPollingState {
  progress: number;
  hasConnectionIssue: boolean;
  isLongWaiting: boolean;
  reset: () => void;
}

export function useTrialResultPolling({
  active,
  expectedOrderIdx,
  onCompleted,
  onFailed,
}: UseTrialResultPollingOptions): TrialResultPollingState {
  const [progress, setProgress] = useState(0);
  const [hasConnectionIssue, setHasConnectionIssue] = useState(false);
  const [isLongWaiting, setIsLongWaiting] = useState(false);
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);

  const reset = useCallback(() => {
    setProgress(0);
    setHasConnectionIssue(false);
    setIsLongWaiting(false);
  }, []);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    onFailedRef.current = onFailed;
  }, [onFailed]);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let pollingTimer: ReturnType<typeof setTimeout> | undefined;
    let consecutiveErrors = 0;
    let effectiveExpectedOrderIdx = expectedOrderIdx;
    const startedAt = Date.now();

    reset();

    const longWaitingTimer = setTimeout(() => {
      if (!cancelled) setIsLongWaiting(true);
    }, FAST_POLLING_DURATION_MS);

    const clearTimers = () => {
      if (pollingTimer !== undefined) clearTimeout(pollingTimer);
      clearTimeout(longWaitingTimer);
    };

    const stopPolling = () => {
      cancelled = true;
      clearTimers();
    };

    const scheduleNextPoll = (poll: () => void) => {
      const elapsedMs = Date.now() - startedAt;
      const intervalMs =
        elapsedMs < FAST_POLLING_DURATION_MS
          ? FAST_POLLING_INTERVAL_MS
          : SLOW_POLLING_INTERVAL_MS;
      pollingTimer = setTimeout(poll, intervalMs);
    };

    const poll = async () => {
      try {
        const result = await getTrialResult();
        if (cancelled) return;

        consecutiveErrors = 0;
        setHasConnectionIssue(false);

        if (
          effectiveExpectedOrderIdx === null ||
          result.orderIdx < effectiveExpectedOrderIdx
        ) {
          scheduleNextPoll(poll);
          return;
        }

        if (result.orderIdx > effectiveExpectedOrderIdx) {
          effectiveExpectedOrderIdx = result.orderIdx;
        }

        setProgress(result.progress);

        if (result.status === "completed") {
          stopPolling();
          onCompletedRef.current(result);
          return;
        }

        if (result.status === "failed") {
          stopPolling();
          onFailedRef.current(result);
          return;
        }
      } catch {
        if (cancelled) return;

        consecutiveErrors += 1;
        if (consecutiveErrors >= CONNECTION_ERROR_THRESHOLD) {
          setHasConnectionIssue(true);
        }
      }

      if (!cancelled) scheduleNextPoll(poll);
    };

    pollingTimer = setTimeout(poll, 0);

    return stopPolling;
  }, [active, expectedOrderIdx, reset]);

  return { progress, hasConnectionIssue, isLongWaiting, reset };
}
